import React, { useState, ReactElement } from 'react';
import { get, partial } from 'lodash';
import {
  ApiKeys,
  CreditCardBrandCode,
  PaymentDetailsCard,
  PaymentProcessor,
  SupportedPaymentMethod,
  PaymentSourceType,
  PaymentSourceInput,
  CreditCardPaymentSource,
} from '@zapier/payment/src/types';
import { Button, Text } from '@zapier/design-system';
import CreditCardForm from '@zapier/payment/src/components/CreditCardForm';
import PaymentInfo from '@zapier/payment/src/components/PaymentInfo';
import PaymentMethodImage from '@zapier/payment/src/components/PaymentMethodImage';
import { useSize } from '@zapier/payment/src/hooks/useSize';

import PaymentMethod from '../PaymentMethod';
import PaymentMethodSelect from '../PaymentMethodSelect';
type Props = {
  /** Payment processor API keys */
  apiKeys: ApiKeys;
  /** Currently select method */
  currentMethod: PaymentSourceType | null;
  /** Error message to show */
  error?: string | undefined | null;
  /** Do not allow user to cancel card edit */
  forceCardUpdate: boolean;
  /** Hide switch component button */
  hideSwitch: boolean;
  /** Show editor */
  isEditing: boolean;
  /** Show loading */
  isEditingInFlight: boolean;
  /** Call to cancel edit */
  onCancelEdit: () => void;
  /** Call to start edit */
  onEdit: () => void;
  /** Call when editor throws an error */
  onErrorEdit: (error: Error) => void;
  /** Call to save current edit */
  onSaveEdit: (b: PaymentSourceInput) => void;
  /** Call to select a payment source */
  onSelectSource: (method: PaymentSourceType) => void;
  /** User's currently saved card details */
  savedSource: CreditCardPaymentSource | null;
  /** Supported payment methods (bank, card etc.) */
  supportedMethods: Array<SupportedPaymentMethod>;
};

const renderLabel = (
  props: Props,
  cardBrand: CreditCardBrandCode,
  isSmall: boolean
) => {
  return props.savedSource && !props.isEditing ? (
    <PaymentInfo paymentSource={props.savedSource} />
  ) : (
    <React.Fragment>
      {isSmall && !props.hideSwitch ? null : (
        <PaymentMethodImage imageType={cardBrand} isSmall={true} />
      )}
      <Text tag="h3" type="subtitle700">
        {props.savedSource ? 'Edit Card' : 'Add Card'}
      </Text>
    </React.Fragment>
  );
};

const renderAside: React.FC<Props> = (props, isSmall: boolean) => {
  if (!props.isEditing) {
    return (
      <Button color="greyAlt" onClick={props.onEdit}>
        {props.savedSource ? `Edit${!isSmall ? ' Payment Method' : ''}` : 'Add'}
      </Button>
    );
  } else {
    return props.hideSwitch ? null : (
      <PaymentMethodSelect
        currentMethod={props.currentMethod}
        onChange={props.onSelectSource}
        supportedMethods={props.supportedMethods}
      />
    );
  }
};

const PaymentMethodCard: React.FC<Props> = props => {
  const [cardBrand, setCardBrand] = useState<
    CreditCardBrandCode | null | undefined
  >(null);
  const size = useSize();
  const isSmall = size === 'small';

  //@ts-ignore savedSource is not a type
  const cardDetails: PaymentDetailsCard = get(props, [
    'savedSource',
    'details',
  ]);

  const cardMethod = props.supportedMethods.find(
    m => m.method === PaymentSourceType.card
  );

  return (
    <PaymentMethod
      isEditing={props.isEditing}
      renderAside={partial(renderAside, props, isSmall)}
      renderLabel={
        //@ts-ignore Lodash issue
        partial(renderLabel, props, cardBrand, isSmall) as () => ReactElement
      }
    >
      <CreditCardForm
        apiKeys={props.apiKeys}
        error={props.error}
        hasCancelButton={!props.forceCardUpdate}
        hasPaymentSource={Boolean(props.savedSource)}
        isInFlight={props.isEditingInFlight}
        onCancel={props.onCancelEdit}
        onCardNumberChange={setCardBrand}
        onError={props.onErrorEdit}
        onSave={props.onSaveEdit}
        paymentProcessor={get(
          cardMethod,
          'paymentProcessor',
          PaymentProcessor.stripe
        )}
        savedCard={cardDetails}
      />
    </PaymentMethod>
  );
};

export default PaymentMethodCard;
